import { useEffect, useRef, useState } from 'react';
import { getContactAvatar, recoverMessageMedia } from '../api/messages';
import { TestoConLink } from './TestoMessaggio';
const DJANGO_BASE = '';
// Con `onReact` ogni reazione è un pulsante: toccarla aggiunge la stessa.
export function Reactions({ reactions, onReact }) {
  if (!reactions || Object.keys(reactions).length === 0) return null;
  const classe = 'inline-flex items-center gap-0.5 bg-canvas rounded-full px-1.5 py-0.5 text-xs border border-line';
  return (
    <div className="flex gap-1 mt-1 flex-wrap">
      {Object.entries(reactions).map(([emoji, senders]) => (onReact
        ? <button key={emoji} type="button" className={classe} aria-label={`Reagisci anche tu con ${emoji}`}
            onClick={e => { e.stopPropagation(); onReact(emoji); }}>
            {emoji} <span className="text-muted">{senders.length}</span>
          </button>
        : <span key={emoji} className={classe}>
            {emoji} <span className="text-muted">{senders.length}</span>
          </span>))}
    </div>
  );
}

// ─── Quoted message preview ───
export function QuotedMessage({ msg }) {
  if (!msg.quoted_body && !msg.quoted_message_id) return null;
  return (
    <div className="border-l-2 border-brand-500 pl-2 mb-1 text-xs text-muted bg-surface-2 rounded py-1 pr-2">
      {msg.quoted_author && <span className="font-semibold text-brand-700">{msg.quoted_author}</span>}
      <p className="truncate">{msg.quoted_body || '...'}</p>
    </div>
  );
}

// ─── Media message component ───
// Con `onOpen` foto e documenti si aprono nel visore dell'app (MediaViewer).
export function MediaMessage({ msg, onOpen }) {
  const [failed, setFailed] = useState(false);
  const [recovered, setRecovered] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const url = recovered || (msg.media_url ? `${DJANGO_BASE}${msg.media_url}` : null);
  const type = msg.message_type;

  if (!url && type !== 'text') {
    return (
      <div className="flex items-center gap-1 text-muted italic text-xs">
        {type === 'image' && '🖼️'}{type === 'video' && '🎬'}{type === 'audio' && '🎵'}
        {type === 'document' && '📄'}{type === 'sticker' && '🏷️'}
        <span className="whitespace-pre-wrap break-words">{type === 'audio' ? 'Messaggio vocale non scaricato' : msg.body || `${type} (non scaricato)`}</span>
        {msg.connection && <button className="underline" disabled={downloading} onClick={async () => {
          setDownloading(true); setError('');
          try { const { data } = await recoverMessageMedia(msg.id); setRecovered(data.media_url); }
          catch (err) { setError(err.response?.data?.detail || 'Recupero non riuscito. Riprova.'); }
          finally { setDownloading(false); }
        }}>{downloading ? 'Scaricamento…' : 'Scarica'}</button>}
        {error && <span role="alert">{error}</span>}
      </div>
    );
  }
  if (!url) return <TestoConLink testo={msg.body} />;

  switch (type) {
    case 'image':
      return (<div>
        <img src={url} alt="" loading="lazy" className="max-w-full max-h-60 rounded-md mb-1 cursor-pointer object-cover"
          role={onOpen ? 'button' : undefined} aria-label={onOpen ? 'Apri la foto' : undefined}
          onClick={e => { if (onOpen) { e.stopPropagation(); onOpen({ ...msg, media_url: url }); } else window.open(url); }} />
        {/* `whitespace-pre-wrap` conserva gli a capo: senza, una didascalia su
            piu' righe arrivava tutta appiccicata in una riga sola. */}
        {msg.body && <TestoConLink testo={msg.body} className="text-sm mt-1 whitespace-pre-wrap break-words" />}
      </div>);
    case 'video':
      return (<div>
        <video controls preload="metadata" src={url} className="max-w-full max-h-60 rounded-md mb-1" />
        {/* `whitespace-pre-wrap` conserva gli a capo: senza, una didascalia su
            piu' righe arrivava tutta appiccicata in una riga sola. */}
        {msg.body && <TestoConLink testo={msg.body} className="text-sm mt-1 whitespace-pre-wrap break-words" />}
      </div>);
    case 'audio':
      return <div><audio controls preload="metadata" src={url} onError={() => setFailed(true)} onLoadedMetadata={() => setFailed(false)} className="max-w-full w-[250px]" aria-label="Messaggio vocale" />{failed && <p role="alert" className="text-xs mt-2">Audio non riproducibile. <a className="underline" href={url} target="_blank" rel="noopener noreferrer">Apri il file audio</a></p>}</div>;
    case 'sticker':
      return <img src={url} alt="" className="w-24 h-24 object-contain" />;
    case 'document':
      // Il nome vero dell'allegato, non «Documento»: un foglio di calcolo
      // arrivava senza nome e si scaricava come file binario senza estensione.
      // `download` fa usare al browser il nome che manda il server.
      if (onOpen) {
        return (<button type="button" onClick={e => { e.stopPropagation(); onOpen({ ...msg, media_url: url }); }}
                        className="flex items-center gap-2 text-info hover:underline text-sm py-1 text-left">
          📄 <span className="break-all">{msg.media_name || msg.body || 'Documento'}</span>
          {msg.media_name && msg.body && <span className="text-muted">— {msg.body}</span>}
        </button>);
      }
      return (<a href={url} download={msg.media_name || undefined} target="_blank"
                 rel="noopener noreferrer"
                 className="flex items-center gap-2 text-info hover:underline text-sm py-1">
        📄 <span className="break-all">{msg.media_name || msg.body || 'Documento'}</span>
        {msg.media_name && msg.body && <span className="text-muted">— {msg.body}</span>}
      </a>);
    default:
      return <TestoConLink testo={msg.body || `[${type}]`} />;
  }
}

// ─── Avatar component with lazy loading ───
const avatarCache = {};
// `author`: nei gruppi la foto è di chi scrive. Prima si chiedeva con la sola
// chat, e ogni mittente mostrava la foto del gruppo.
export function Avatar({ contactId, author = '', name, size = 32, initialUrl = '' }) {
  const anchor = useRef(null);
  const chiave = author ? `${contactId}:${author}` : contactId;
  const [url, setUrl] = useState(avatarCache[chiave] || initialUrl || '');
  const refreshed = useRef(false);

  useEffect(() => {
    let cancelled = false;
    refreshed.current = false;
    setUrl(avatarCache[chiave] || initialUrl || '');
    const load = () => {
      if (avatarCache[chiave] !== undefined) { setUrl(avatarCache[chiave]); return; }
      if (initialUrl) return;
      getContactAvatar(contactId, false, author).then(({ data }) => {
        if (data.url) avatarCache[chiave] = data.url;
        if (!cancelled) setUrl(data.url || '');
      }).catch(() => {});
    };
    const observer = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) { observer.disconnect(); load(); }
    }, { rootMargin: '100px' }) : null;
    if (observer && anchor.current) observer.observe(anchor.current); else load();
    return () => { cancelled = true; observer?.disconnect(); };
  }, [contactId, author, chiave, initialUrl]);

  if (url) {
    return <img src={url} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={async () => {
      delete avatarCache[chiave]; setUrl('');
      if (refreshed.current) return;
      refreshed.current = true;
      try { const { data } = await getContactAvatar(contactId, true, author); if (data.url) { avatarCache[chiave] = data.url; setUrl(data.url); } } catch (_) {}
    }} className="rounded-full object-cover shrink-0" style={{ width: size, height: size }} />;
  }
  // Fallback: initials
  const initials = (name || '?').split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  return (
    <div ref={anchor} className="rounded-full bg-brand-50 text-brand-700 flex items-center justify-center text-xs font-bold shrink-0"
      style={{ width: size, height: size }}>{initials}</div>
  );
}

